import type { ChatHistory, ChatMessage } from "@/lib/types";
import { stripHiddenPromptSuffix } from "@/utility/chatHelpers";

/**
 * Restores the user's last chat session across taskpane reloads.
 *
 * The active chat history id is persisted per (organizationId, projectId) in
 * localStorage. On chat load the stored id is rehydrated through the same
 * getChatThread + thread→messages transformation the past-chat drawer uses.
 */

const LAST_CHAT_KEY_PREFIX = "jenly_addin_last_chat_v1";

/** localStorage key for the last active chat of an org + project pair. */
export const buildLastChatStorageKey = (organizationId: string, projectId: string): string =>
  `${LAST_CHAT_KEY_PREFIX}_${organizationId}_${projectId}`;

/** Remembers `chatHistoryId` as the last active chat for the org/project. */
export const storeLastChatId = (organizationId: string, projectId: string, chatHistoryId: string): void => {
  try {
    window.localStorage.setItem(buildLastChatStorageKey(organizationId, projectId), chatHistoryId);
  } catch {
    // Storage can be unavailable (privacy mode / quota); restoring is best-effort.
  }
};

/** Returns the stored last chat id for the org/project, or null. */
export const getStoredLastChatId = (organizationId: string, projectId: string): string | null => {
  try {
    return window.localStorage.getItem(buildLastChatStorageKey(organizationId, projectId));
  } catch {
    return null;
  }
};

/** Drops the stored last chat id (new chat, deleted chat, stale thread). */
export const clearStoredLastChatId = (organizationId: string, projectId: string): void => {
  try {
    window.localStorage.removeItem(buildLastChatStorageKey(organizationId, projectId));
  } catch {
    // Best-effort.
  }
};

/**
 * Flattens a stored chat thread into the alternating user/assistant ChatMessage
 * list used by the chat page. Identical to the handleOpenPastChat
 * transformation in ChatPage.tsx (kept in one place so the restore path and the
 * past-chat drawer can never drift).
 */
export const chatThreadToMessages = (thread: ChatHistory[]): ChatMessage[] =>
  thread.flatMap((turn) => {
    const cleanQuestion = stripHiddenPromptSuffix(turn.question);
    return [
      {
        id: `${turn.id}-q`,
        role: "user" as const,
        content: cleanQuestion,
        timestamp: new Date(turn.created_at),
      },
      {
        id: turn.id,
        role: "assistant" as const,
        content: turn.answer,
        sources: turn.sources,
        timestamp: new Date(turn.created_at),
      },
    ];
  });
